import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  Music2,
  Upload,
  ShieldCheck,
  Check,
  Handshake,
  Sparkles,
  Radio,
  Globe as GlobeIcon,
  Mail,
  Ban,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * /submit — public music submission portal. The full DRR Music Submission
 * & Airplay Policy is visible on-page so bands read it before uploading
 * (which is exactly what fair play requires). Everything fits on a single
 * dedicated page — policy above, form below, success screen swaps in on
 * successful upload.
 *
 * The form talks to POST /api/submissions (multipart, includes audio file).
 * Backend caps at 20 MB and stores as base64 in the ``band_submissions``
 * collection. Admin panel has a dedicated card for review.
 */
export default function Submit() {
  const [genres, setGenres] = useState([]);
  const [loadingGenres, setLoadingGenres] = useState(true);

  const [form, setForm] = useState({
    band_name: "",
    song_title: "",
    genre: "",
    country: "",
    email: "",
    social_facebook: "",
    social_instagram: "",
    social_twitter: "",
    social_website: "",
    social_other: "",
    message: "",
    not_ai: false,
    agreed: false,
  });
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null); // { id }

  // Fetch allowed genres so the dropdown always matches server enforcement.
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/submissions/genres`)
      .then((res) => {
        if (cancelled) return;
        setGenres(res?.data?.genres || []);
      })
      .catch(() => {
        /* silent — form still submits, backend validates */
      })
      .finally(() => {
        if (!cancelled) setLoadingGenres(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const update = (k) => (e) => {
    const val = e?.target?.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: val }));
  };

  const canSubmit = useMemo(() => {
    return (
      form.band_name.trim() &&
      form.song_title.trim() &&
      form.genre &&
      form.country.trim() &&
      form.email.trim() &&
      form.not_ai &&
      form.agreed &&
      !!file &&
      !submitting
    );
  }, [form, file, submitting]);

  const handleFile = (e) => {
    const f = e?.target?.files?.[0] || null;
    if (!f) {
      setFile(null);
      return;
    }
    // 20MB cap — matches server. Warn early so bands don't wait for a
    // rejected upload after 30s.
    const MAX = 20 * 1024 * 1024;
    if (f.size > MAX) {
      toast.error(
        `File is ${(f.size / (1024 * 1024)).toFixed(1)} MB — max 20 MB. Please compress or trim.`,
      );
      e.target.value = "";
      return;
    }
    setFile(f);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => {
      fd.append(k, k === "agreed" ? (v ? "true" : "false") : String(v ?? ""));
    });
    fd.append("file", file);
    try {
      const res = await axios.post(`${API}/submissions`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      if (res?.data?.ok) {
        setSuccess({ id: res.data.id });
        // Scroll top so listeners see the success screen.
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        toast.error("Something went wrong — try again");
      }
    } catch (err) {
      const detail =
        err?.response?.data?.detail || "Submission failed. Try again in a moment.";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return <SuccessScreen id={success.id} />;
  }

  return (
    <div
      className="min-h-screen bg-zinc-950 text-zinc-100"
      data-testid="submit-page"
    >
      <Header />
      <div className="px-4 md:px-10 pt-8 pb-2">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-xs tracking-widest uppercase text-zinc-300 hover:text-red-500 transition-colors"
          data-testid="submit-back-home"
        >
          <ArrowLeft className="w-3 h-3" /> Back to home
        </Link>
      </div>

      {/* ===== HERO ===== */}
      <section
        className="relative border-b border-zinc-800 bg-black overflow-hidden"
        data-testid="submit-hero"
      >
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage:
              "repeating-linear-gradient(-45deg, transparent 0 14px, rgba(220,38,38,1) 14px 16px)",
          }}
        />
        <div className="relative max-w-6xl mx-auto px-4 md:px-10 py-14 md:py-20">
          <div className="text-xs tracking-[0.4em] text-red-500 uppercase mb-3 font-display flex items-center gap-2">
            <Music2 className="w-3.5 h-3.5" />
            //_SUBMIT_YOUR_MUSIC · SINCE_2007
          </div>
          <h1
            className="font-display text-5xl md:text-7xl leading-[0.9] tracking-tighter text-zinc-50 glitch"
            data-testid="submit-hero-title"
          >
            GET YOUR BAND ON <span className="text-red-600">THE AIR.</span>
          </h1>
          <p className="mt-6 max-w-3xl text-base md:text-lg text-zinc-300 leading-snug">
            Over 80% of our programming is dedicated to independent rock and
            metal artists. If your track fits the signal, we&apos;ll listen —
            and if it hits, we&apos;ll spin it to a global audience.
          </p>
          <p className="mt-3 text-sm text-zinc-500 leading-relaxed">
            Read the Submission &amp; Airplay Policy below before filling out
            the form. Fair play, mutual respect, and real support both ways.
          </p>
          <div
            className="mt-6 border-2 border-red-600 bg-red-950/30 p-4 md:p-5 flex items-start gap-3"
            data-testid="submit-no-ai-banner"
          >
            <Ban className="w-5 h-5 md:w-6 md:h-6 text-red-500 shrink-0 mt-0.5" strokeWidth={2.5} />
            <div>
              <div className="text-[10px] md:text-xs tracking-[0.35em] text-red-500 uppercase font-display mb-1">
                //_HUMAN_MADE_ONLY
              </div>
              <div className="font-display text-lg md:text-xl text-zinc-50 leading-tight">
                AI-generated tracks are <span className="text-red-500">NOT</span> accepted.
              </div>
              <p className="text-xs md:text-sm text-zinc-300 mt-1.5 leading-relaxed">
                DRR spins real music made by real people. Any submission
                detected as AI-generated will be rejected on the spot — no
                exceptions.
              </p>
            </div>
          </div>
          <div className="mt-6 flex items-center gap-2 flex-wrap">
            <Pill icon={<Radio className="w-3 h-3" />} label="Rock / Metal Only" />
            <Pill icon={<Ban className="w-3 h-3" />} label="No AI Tracks" />
            <Pill icon={<Sparkles className="w-3 h-3" />} label="320 kbps preferred" />
            <Pill icon={<ShieldCheck className="w-3 h-3" />} label="No signup" />
            <Pill icon={<Handshake className="w-3 h-3" />} label="Mutual promo" />
          </div>
        </div>
      </section>

      {/* ===== POLICY ===== */}
      <PolicySection />


      {/* ===== SUBMISSION FORM ===== */}
      <section
        id="form"
        className="border-b border-zinc-800 bg-black"
        data-testid="submit-form-section"
      >
        <div className="max-w-4xl mx-auto px-4 md:px-10 py-14 md:py-20">
          <div className="text-xs tracking-[0.4em] text-red-500 uppercase mb-3 font-display">
            //_THE_FORM
          </div>
          <h2 className="font-display text-3xl md:text-5xl leading-tight tracking-tight text-zinc-50 mb-2">
            SEND US <span className="text-red-600">THE TRACK.</span>
          </h2>
          <p className="text-sm text-zinc-400 mb-8 max-w-2xl">
            Everything on one page. Fill it in, upload the audio, agree to
            the policy, and hit submit. Reviewed by our team — no bots.
          </p>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="submit-form">
            <Field label="Band Name" required testId="submit-field-band">
              <input
                type="text"
                value={form.band_name}
                onChange={update("band_name")}
                maxLength={120}
                required
                placeholder="e.g. The Devil's Radio"
                className="w-full bg-zinc-950 border border-zinc-800 focus:border-red-600 focus:outline-none px-3 py-2.5 text-sm text-zinc-100 font-mono"
                data-testid="submit-input-band"
              />
            </Field>

            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Song Title" required testId="submit-field-song">
                <input
                  type="text"
                  value={form.song_title}
                  onChange={update("song_title")}
                  maxLength={160}
                  required
                  placeholder="e.g. Signal in the Noise"
                  className="w-full bg-zinc-950 border border-zinc-800 focus:border-red-600 focus:outline-none px-3 py-2.5 text-sm text-zinc-100 font-mono"
                  data-testid="submit-input-song"
                />
              </Field>
              <Field label="Genre" required testId="submit-field-genre">
                <select
                  value={form.genre}
                  onChange={update("genre")}
                  required
                  disabled={loadingGenres}
                  className="w-full bg-zinc-950 border border-zinc-800 focus:border-red-600 focus:outline-none px-3 py-2.5 text-sm text-zinc-100 font-mono disabled:opacity-50"
                  data-testid="submit-input-genre"
                >
                  <option value="">// pick a genre</option>
                  {genres.map((g) => (
                    <option key={g} value={g}>
                      {g}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Country / Location" required testId="submit-field-country">
                <input
                  type="text"
                  value={form.country}
                  onChange={update("country")}
                  maxLength={80}
                  required
                  placeholder="e.g. Cleveland, OH · USA"
                  className="w-full bg-zinc-950 border border-zinc-800 focus:border-red-600 focus:outline-none px-3 py-2.5 text-sm text-zinc-100 font-mono"
                  data-testid="submit-input-country"
                />
              </Field>
              <Field label="Contact Email" required testId="submit-field-email">
                <input
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  maxLength={160}
                  required
                  placeholder="you@yourband.com"
                  className="w-full bg-zinc-950 border border-zinc-800 focus:border-red-600 focus:outline-none px-3 py-2.5 text-sm text-zinc-100 font-mono"
                  data-testid="submit-input-email"
                />
              </Field>
            </div>

            {/* Social links — all optional */}
            <div>
              <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase mb-3 font-display">
                //_socials · at least one preferred
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <SocialField
                  label="Facebook"
                  value={form.social_facebook}
                  onChange={update("social_facebook")}
                  placeholder="facebook.com/yourband"
                  testId="submit-input-fb"
                />
                <SocialField
                  label="Instagram"
                  value={form.social_instagram}
                  onChange={update("social_instagram")}
                  placeholder="instagram.com/yourband"
                  testId="submit-input-ig"
                />
                <SocialField
                  label="X / Twitter"
                  value={form.social_twitter}
                  onChange={update("social_twitter")}
                  placeholder="x.com/yourband"
                  testId="submit-input-tw"
                />
                <SocialField
                  label="Website"
                  value={form.social_website}
                  onChange={update("social_website")}
                  placeholder="yourband.com"
                  testId="submit-input-web"
                />
                <SocialField
                  label="Other (Bandcamp, Spotify, etc.)"
                  value={form.social_other}
                  onChange={update("social_other")}
                  placeholder="bandcamp.com/yourband or link.tree/…"
                  testId="submit-input-other"
                />
              </div>
            </div>

            {/* Audio file upload */}
            <Field
              label="Song File"
              required
              hint="MP3 preferred (320 kbps). WAV / M4A / FLAC / OGG also accepted. Max 20 MB."
              testId="submit-field-file"
            >
              <div className="relative">
                <input
                  type="file"
                  accept="audio/mpeg,audio/mp3,audio/wav,audio/x-wav,audio/mp4,audio/x-m4a,audio/ogg,audio/flac,audio/aac,audio/*"
                  onChange={handleFile}
                  required
                  className="w-full bg-zinc-950 border border-zinc-800 focus:border-red-600 focus:outline-none px-3 py-2 text-xs text-zinc-300 file:bg-red-600 file:text-white file:border-0 file:px-4 file:py-2 file:mr-3 file:text-[10px] file:tracking-widest file:uppercase file:font-display file:cursor-pointer hover:file:bg-red-500"
                  data-testid="submit-input-file"
                />
                {file && (
                  <div
                    className="mt-2 text-xs text-zinc-400 flex items-center gap-2 font-mono"
                    data-testid="submit-file-info"
                  >
                    <Check className="w-3 h-3 text-red-500" />
                    {file.name} · {(file.size / (1024 * 1024)).toFixed(2)} MB
                  </div>
                )}
              </div>
            </Field>

            {/* Optional message */}
            <Field
              label="Message to the Station (optional)"
              hint="Anything you want the DJs to know — story behind the track, release date, tour dates, links to other work."
              testId="submit-field-message"
            >
              <textarea
                value={form.message}
                onChange={update("message")}
                maxLength={1200}
                rows={4}
                placeholder="Tell us about the band, the track, or anything else worth mentioning…"
                className="w-full bg-zinc-950 border border-zinc-800 focus:border-red-600 focus:outline-none px-3 py-2.5 text-sm text-zinc-100 font-mono leading-relaxed resize-y"
                data-testid="submit-input-message"
              />
              <div className="mt-1 text-[10px] tracking-widest text-zinc-500 text-right font-mono">
                {form.message.length}/1200
              </div>
            </Field>

            {/* No-AI attestation — bands must explicitly confirm the
                track was not generated by AI. Separate from the general
                policy agreement so it can't be missed / auto-clicked. */}
            <label
              className={`flex items-start gap-3 border-2 p-4 cursor-pointer transition-colors ${
                form.not_ai
                  ? "border-red-600 bg-red-950/30"
                  : "border-zinc-800 bg-zinc-950 hover:border-zinc-700"
              }`}
              data-testid="submit-field-not-ai"
            >
              <input
                type="checkbox"
                checked={form.not_ai}
                onChange={update("not_ai")}
                required
                className="mt-1 w-4 h-4 accent-red-600 shrink-0"
                data-testid="submit-input-not-ai"
              />
              <div className="min-w-0">
                <div className="text-sm text-zinc-100 font-display tracking-wide flex items-center gap-2">
                  <Ban className="w-4 h-4 text-red-500" />
                  This track is <span className="text-red-500">not</span> AI-generated.
                </div>
                <div className="mt-1 text-xs text-zinc-400 leading-relaxed">
                  I confirm this song was written, performed, and produced by
                  real people — not composed, sung, or generated by AI. False
                  attestations get the artist permanently blocked from DRR.
                </div>
              </div>
            </label>

            {/* Policy agreement */}
            <label
              className={`flex items-start gap-3 border-2 p-4 cursor-pointer transition-colors ${
                form.agreed
                  ? "border-red-600 bg-red-950/30"
                  : "border-zinc-800 bg-zinc-950 hover:border-zinc-700"
              }`}
              data-testid="submit-field-agreement"
            >
              <input
                type="checkbox"
                checked={form.agreed}
                onChange={update("agreed")}
                required
                className="mt-1 w-4 h-4 accent-red-600 shrink-0"
                data-testid="submit-input-agreed"
              />
              <div className="min-w-0">
                <div className="text-sm text-zinc-100 font-display tracking-wide">
                  I&apos;ve read the Submission &amp; Airplay Policy above and
                  agree to its terms.
                </div>
                <div className="mt-1 text-xs text-zinc-400 leading-relaxed">
                  I understand submission does not guarantee airplay, and if
                  selected, I&apos;ll actively support Digital Revolution
                  Radio on social media as part of the mutual growth
                  agreement.
                </div>
              </div>
            </label>

            <button
              type="submit"
              disabled={!canSubmit}
              className={`w-full md:w-auto inline-flex items-center justify-center gap-3 border-2 font-display tracking-[0.25em] uppercase text-base px-8 py-4 transition-colors ${
                canSubmit
                  ? "border-red-600 bg-red-600 text-white hover:bg-white hover:text-black"
                  : "border-zinc-800 bg-zinc-900 text-zinc-500 cursor-not-allowed"
              }`}
              data-testid="submit-form-submit"
            >
              <Upload className="w-5 h-5" strokeWidth={2.5} />
              {submitting ? "Uploading…" : "Submit the Track 🤘"}
            </button>

            <p className="text-xs text-zinc-500 leading-relaxed">
              Upload may take up to a minute depending on your connection.
              You&apos;ll see a confirmation once it&apos;s in our queue.
            </p>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
}

/* ============================================================================
   Sub-components
   ========================================================================= */

function Pill({ icon, label }) {
  return (
    <span className="inline-flex items-center gap-1.5 border border-zinc-700 bg-zinc-950/70 px-2.5 py-1 text-[10px] tracking-[0.25em] uppercase text-zinc-300 font-display">
      {icon}
      {label}
    </span>
  );
}

function Field({ label, required, hint, children, testId }) {
  return (
    <label className="flex flex-col gap-1.5" data-testid={testId}>
      <span className="text-[10px] tracking-[0.3em] text-zinc-400 uppercase font-display">
        //_{label.toLowerCase().replace(/\s+/g, "_")}
        {required && <span className="text-red-500 ml-1">*</span>}
      </span>
      {children}
      {hint && (
        <span className="text-[11px] text-zinc-500 leading-snug">{hint}</span>
      )}
    </label>
  );
}

function SocialField({ label, value, onChange, placeholder, testId }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] tracking-[0.28em] text-zinc-500 uppercase font-mono">
        {label}
      </span>
      <input
        type="text"
        value={value}
        onChange={onChange}
        maxLength={240}
        placeholder={placeholder}
        className="w-full bg-zinc-950 border border-zinc-800 focus:border-red-600 focus:outline-none px-3 py-2 text-sm text-zinc-100 font-mono"
        data-testid={testId}
      />
    </label>
  );
}

/* ============================================================================
   Policy section — visible on-page before the form
   ========================================================================= */

const POLICY_SECTIONS = [
  {
    slug: "human-made",
    icon: <Ban className="w-4 h-4" />,
    title: "Human-Made Only — No AI",
    body: [
      "Digital Revolution Radio spins real music, made by real people. AI-generated tracks are not accepted under any circumstance.",
      "This includes fully AI-composed songs, AI-generated vocals, and tracks where AI produced the core musical performance.",
      "Any submission we identify as AI-generated will be rejected without review. Repeat offenders will be blocked from future submissions.",
    ],
  },
  {
    slug: "submission",
    icon: <Music2 className="w-4 h-4" />,
    title: "Submission Guidelines",
    body: [
      "All submissions must fall within the rock or metal genres — including classic rock, hard rock, heavy metal, hair metal, thrash, metalcore, and related styles.",
      "Tracks must be high-quality audio. Minimum 320 kbps MP3 preferred (WAV / FLAC also accepted for higher fidelity).",
      "Please include: band name, song title, genre, country / location, and social media links.",
      "Submissions that do not meet these requirements may not be reviewed.",
    ],
  },
  {
    slug: "airplay",
    icon: <Radio className="w-4 h-4" />,
    title: "Airplay & Rotation",
    body: [
      "Submission of music does not guarantee airplay. Tracks selected for rotation are chosen based on:",
      { list: ["Audio quality", "Genre fit", "Overall production and performance", "Listener engagement and response"] },
      "Digital Revolution Radio reserves the right to determine all programming and rotation decisions.",
    ],
  },
  {
    slug: "support",
    icon: <Handshake className="w-4 h-4" />,
    title: "Artist Support & Promotion Expectations",
    body: [
      "Digital Revolution Radio is built on mutual support. If your music is selected for airplay, we expect artists to actively support the platform that is supporting them. This includes:",
      {
        list: [
          "Following our official social media platforms",
          "Sharing posts when your music is played or featured",
          "Promoting Digital Revolution Radio to your fanbase",
          "Engaging with our content and community",
        ],
      },
      "Artists who actively support the station may receive: increased airplay rotation, featured placement on our website and social platforms, and opportunities for interviews or special segments.",
    ],
  },
  {
    slug: "growth",
    icon: <Sparkles className="w-4 h-4" />,
    title: "Commitment to Growth",
    body: [
      "Over 80% of our programming is dedicated to independent artists. We are committed to helping bands grow — but success is a shared effort.",
      "Artists who consistently engage and promote the station help strengthen the entire community and create more opportunities for everyone involved.",
    ],
  },
];

function PolicySection() {
  return (
    <section
      className="border-b border-zinc-800 bg-zinc-950"
      data-testid="submit-policy-section"
    >
      <div className="max-w-6xl mx-auto px-4 md:px-10 py-14 md:py-20">
        <div className="text-xs tracking-[0.4em] text-red-500 uppercase mb-3 font-display flex items-center gap-2">
          <ShieldCheck className="w-3.5 h-3.5" />
          //_SUBMISSION_&amp;_AIRPLAY_POLICY
        </div>
        <h2 className="font-display text-3xl md:text-5xl leading-tight tracking-tight text-zinc-50 mb-3">
          READ THIS <span className="text-red-600">FIRST.</span>
        </h2>
        <p className="text-sm text-zinc-400 max-w-3xl mb-8 md:mb-10 leading-relaxed">
          We&apos;re proud to support independent rock and metal artists
          worldwide. Since 2007, our mission has been to provide a platform
          where unsigned and emerging bands can be heard, discovered, and
          appreciated by a global audience. We take every submission
          seriously — and here&apos;s what fair play looks like on our side
          and yours.
        </p>

        <div
          className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-zinc-800"
          data-testid="submit-policy-grid"
        >
          {POLICY_SECTIONS.map((s, i) => (
            <div
              key={s.slug}
              className={`p-5 md:p-6 bg-black flex flex-col gap-3 ${
                i % 2 === 0 ? "md:border-r border-zinc-800" : ""
              } ${i < POLICY_SECTIONS.length - 2 ? "border-b border-zinc-800" : ""}`}
              data-testid={`submit-policy-${s.slug}`}
            >
              <div className="flex items-center gap-2">
                <span className="text-red-500">{s.icon}</span>
                <span className="text-[10px] tracking-[0.3em] text-red-500 uppercase font-display">
                  //_{s.slug}
                </span>
              </div>
              <h3 className="font-display text-xl md:text-2xl leading-tight tracking-tight text-zinc-50">
                {s.title}
              </h3>
              <div className="text-sm text-zinc-300 leading-relaxed space-y-2">
                {s.body.map((chunk, idx) =>
                  typeof chunk === "string" ? (
                    <p key={idx}>{chunk}</p>
                  ) : (
                    <ul
                      key={idx}
                      className="list-none pl-0 space-y-1 text-zinc-400"
                    >
                      {chunk.list.map((li) => (
                        <li key={li} className="flex items-start gap-2">
                          <span className="text-red-500 font-mono shrink-0">▮</span>
                          <span>{li}</span>
                        </li>
                      ))}
                    </ul>
                  ),
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Closing */}
        <div className="mt-10 md:mt-14 border-2 border-red-600 bg-black p-6 md:p-8">
          <div className="font-display text-xl md:text-2xl leading-tight tracking-tight text-zinc-50 mb-2">
            Digital Revolution Radio is more than just a station.
          </div>
          <p className="text-sm text-zinc-300 leading-relaxed max-w-3xl">
            It&apos;s a platform built on passion, dedication, and respect
            for the music and the artists behind it. We appreciate every
            band that submits their work and stands with us as we continue
            to grow the independent rock and metal scene worldwide.
          </p>
          <div className="mt-4 font-display text-lg text-red-500 tracking-[0.15em]">
            Let&apos;s work together. Let&apos;s grow together. Let&apos;s
            make this huge. 🤘
          </div>
          <div className="mt-2 text-[10px] tracking-[0.3em] text-zinc-500 uppercase font-display">
            — Digital Revolution Radio
          </div>
        </div>

        <div className="mt-8 flex justify-center">
          <a
            href="#form"
            className="inline-flex items-center gap-2 border-2 border-red-600 bg-red-600 text-white hover:bg-white hover:text-black px-6 py-3 text-sm tracking-widest uppercase font-display transition-colors"
            data-testid="submit-jump-to-form"
          >
            <Upload className="w-4 h-4" strokeWidth={2.5} />
            I READ IT — TAKE ME TO THE FORM
          </a>
        </div>
      </div>
    </section>
  );
}

/* ============================================================================
   Success screen — replaces the form on submit success
   ========================================================================= */

function SuccessScreen({ id }) {
  return (
    <div
      className="min-h-screen bg-zinc-950 text-zinc-100"
      data-testid="submit-success"
    >
      <Header />
      <section className="relative border-b border-zinc-800 bg-black overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.08] pointer-events-none"
          style={{
            backgroundImage:
              "repeating-linear-gradient(45deg, transparent 0 16px, rgba(220,38,38,1) 16px 18px)",
          }}
        />
        <div className="relative max-w-3xl mx-auto px-4 md:px-10 py-20 md:py-32 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 md:w-24 md:h-24 border-4 border-red-600 mb-8" data-testid="submit-success-check">
            <Check className="w-10 h-10 md:w-12 md:h-12 text-red-500" strokeWidth={3} />
          </div>
          <div className="text-xs tracking-[0.4em] text-red-500 uppercase mb-3 font-display">
            //_SUBMISSION_RECEIVED
          </div>
          <h1 className="font-display text-4xl md:text-6xl leading-[0.9] tracking-tighter text-zinc-50 mb-6">
            THE TRACK IS <span className="text-red-600">IN THE QUEUE.</span>
          </h1>
          <p className="text-base md:text-lg text-zinc-300 leading-relaxed mb-6 max-w-xl mx-auto">
            Our team reviews every submission personally. If your track is
            selected for rotation, we&apos;ll reach out via the email you
            provided.
          </p>
          <p className="text-sm text-zinc-500 leading-relaxed mb-8 max-w-xl mx-auto">
            Meanwhile — follow us on social, tune in live, and hang out in{" "}
            <Link
              to="/chat"
              className="text-red-500 hover:text-white transition-colors underline underline-offset-2"
            >
              The Pit
            </Link>
            . That mutual support the policy mentioned? It starts today. 🤘
          </p>
          <div className="text-[10px] tracking-[0.3em] text-zinc-600 uppercase font-mono mb-8">
            //_submission_id · {id?.slice(0, 8)}
          </div>
          <div className="flex items-center justify-center gap-2 flex-wrap">
            <Link
              to="/"
              className="inline-flex items-center gap-2 border-2 border-red-600 bg-red-600 text-white hover:bg-white hover:text-black px-5 py-3 text-xs tracking-widest uppercase font-display transition-colors"
              data-testid="submit-success-home"
            >
              <Radio className="w-4 h-4" /> TUNE IN NOW
            </Link>
            <Link
              to="/chat"
              className="inline-flex items-center gap-2 border-2 border-zinc-700 text-zinc-100 hover:border-red-600 hover:text-red-500 px-5 py-3 text-xs tracking-widest uppercase font-display transition-colors"
              data-testid="submit-success-chat"
            >
              <Mail className="w-4 h-4" /> JOIN THE PIT
            </Link>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="inline-flex items-center gap-2 border-2 border-zinc-700 text-zinc-100 hover:border-red-600 hover:text-red-500 px-5 py-3 text-xs tracking-widest uppercase font-display transition-colors"
              data-testid="submit-success-another"
            >
              <GlobeIcon className="w-4 h-4" /> SUBMIT ANOTHER
            </button>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
